import type { Ref } from 'vue';

import { ComputeInstanceSize } from '@clovyr/pollen';
import { getWNextPricing } from '@clovyr/pollen/fixtures/pricing';
import type { Manifest } from '@clovyr/pollen/manifest';

// Module-level cache for prices: { [cacheKey]: price }
const priceCache: Record<string, string | Promise<string>> = {};

export function formatWNextPrice(price: string | number): string {
  const numericPrice = typeof price === 'number' ? price : Number.parseFloat(price);
  if (Number.isNaN(numericPrice)) {
    return String(price);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericPrice);
}

export function usePrice(
  app: Ref<Manifest | undefined>,
  size?: Ref<string | undefined>,
  salesTier?: Ref<string | undefined>,
  billingPeriod?: Ref<string | undefined>
) {
  /**
   * The WNext-specific price for the selected app
   */
  const wnextAppPrice = ref('20'); // default to $20

  const fetchPrice = async () => {
    if (!app.value?.metadata) {
      // default to $20 if not found for whatever reason
      wnextAppPrice.value = '20';
      return;
    }
    const appId = app.value.metadata.id;
    const computeSize = (
      size?.value ||
      app.value.deployment?.compute?.size ||
      ComputeInstanceSize.Small
    ).toLowerCase() as ComputeInstanceSize;
    const cacheKey = [appId, computeSize, salesTier?.value || '', billingPeriod?.value || '']
      .filter(Boolean)
      .join('-');

    const c = priceCache[cacheKey];
    if (c) {
      if (typeof c === 'object' && c instanceof Promise) {
        wnextAppPrice.value = await c;
        return;
      }
      if (typeof c === 'string') {
        wnextAppPrice.value = c;
        return;
      }
    }
    priceCache[cacheKey] = getWNextPricing(appId, computeSize, {
      salesTier: salesTier?.value,
      billingPeriod: billingPeriod?.value,
    }).then((instanceType) => {
      return instanceType[1].toString();
    });
    const price = await priceCache[cacheKey];
    wnextAppPrice.value = price;
    priceCache[cacheKey] = price;
  };

  void fetchPrice();
  watch(
    [app, size, salesTier, billingPeriod].filter((o) => !!o),
    fetchPrice
  );

  return {
    wnextAppPrice,
  };
}
